import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import type {IdentityLifecycleRequest, RehireRequest, UpdateReactivationDateRequest, Identity} from '@/types/identity'
import {ACCOUNT_STATUS_EVENTS_QUERY_KEY} from '@/hooks/use-account-status-events'
import {useQueryClient} from '@tanstack/react-query'
import {IDENTITY_LIFECYCLE_EVENTS_QUERY_KEY} from '@/hooks/use-identity-lifecycle-events'
import {
    IDENTITIES_KEY,
    IDENTITY_KEY,
    IDENTITY_SUBKEY_ACCOUNTS,
    IDENTITY_SUBKEY_ASSIGNMENTS,
    IDENTITY_SUBKEY_FG_ASSIGNMENTS,
    IDENTITY_SUBKEY_TIMELINE,
} from './useIdentity'

export interface UseIdentityMutationsOptions {
    onLifecycleSuccess?: () => void
    onRehireSuccess?: () => void
    onReactivationDateSuccess?: () => void
    onEvaluateRulesSuccess?: () => void
    onRevokeSuccess?: () => void
    onRevokeFgSuccess?: () => void
    onCreateAccountSuccess?: () => void
    onAccountActionSettled?: (accountId: string) => void
    accountActionReason?: string
    closeLifecycleDialog?: () => void
    closeRehireDialog?: () => void
    closeReactivationDateDialog?: () => void
    closeRevokeAssignmentDialog?: () => void
    closeRevokeFgDialog?: () => void
    closeCreateAccountDialog?: () => void
    closeDisableAccountDialog?: () => void
    closeEnableAccountDialog?: () => void
    closeDeleteAccountDialog?: () => void
    lifecycleSuccessMessage?: string
}

/**
 * Composite mutation hook for the IdentityDetail page. Bundles every write
 * operation against {@code services.identities.*}, {@code services.accounts.*},
 * and {@code services.functionGroupAssignments.*} so the page body never has
 * to derive query-invalidation strings or success-toast keys inline.
 *
 * Each returned member is a stock TanStack mutation result with consistent
 * invalidation:
 *
 * - {@code lifecycle}            — updates lifecycle state; invalidates
 *                                  detail + list + lifecycle-events.
 * - {@code rehire}                — TERMINATED → SUSPENDED (awaiting entry)
 *                                  or ACTIVE; invalidates detail + list.
 * - {@code reactivationDate}      — edit-in-place on SUSPENDED.
 * - {@code evaluateRules}         — Mover trigger; flips the calculation flag
 *                                  on success via {@code onEvaluateRulesSuccess}.
 * - {@code revoke}                — revoke direct AP assignment.
 * - {@code revokeFg}              — revoke function-group assignment.
 * - {@code createAccount}         — create account on a target system.
 * - {@code disableAccount}        — disable account; invalidates accounts +
 *                                  account-status-events.
 * - {@code enableAccount}         — enable account; symmetric to disable.
 * - {@code deleteAccount}         — delete account / cancel pending creation.
 */
export function useIdentityMutations(
    id: string | undefined,
    options: UseIdentityMutationsOptions = {},
) {
    const services = useServices()
    const i18n = useI18n()
    const queryClient = useQueryClient()
    const {
        onLifecycleSuccess,
        onRehireSuccess,
        onReactivationDateSuccess,
        onEvaluateRulesSuccess,
        onRevokeSuccess,
        onRevokeFgSuccess,
        onCreateAccountSuccess,
        onAccountActionSettled,
        accountActionReason,
        closeLifecycleDialog,
        closeRehireDialog,
        closeReactivationDateDialog,
        closeRevokeAssignmentDialog,
        closeRevokeFgDialog,
        closeCreateAccountDialog,
        closeDisableAccountDialog,
        closeEnableAccountDialog,
        closeDeleteAccountDialog,
        lifecycleSuccessMessage,
    } = options

    const accountsQueryKey = [IDENTITY_KEY, id, IDENTITY_SUBKEY_ACCOUNTS]

    const lifecycle = useEntityMutation<Identity, Error, IdentityLifecycleRequest>({
        mutationFn: (request) => services.identities.updateLifecycle(id!, request),
        invalidate: [
            [IDENTITY_KEY, id],
            [IDENTITIES_KEY],
            [IDENTITY_LIFECYCLE_EVENTS_QUERY_KEY, id],
        ],
        successMessage: lifecycleSuccessMessage ?? i18n.identities.lifecycleUpdated,
        closeDialog: closeLifecycleDialog,
        onSuccess: () => {
            onLifecycleSuccess?.()
        },
    })

    const rehire = useEntityMutation<Identity, Error, RehireRequest>({
        mutationFn: (request) => services.identities.rehire(id!, request),
        invalidate: [[IDENTITY_KEY, id], [IDENTITIES_KEY]],
        successMessage: i18n.identities.rehireSubmitted,
        closeDialog: closeRehireDialog,
        onSuccess: () => {
            onRehireSuccess?.()
        },
    })

    const reactivationDate = useEntityMutation<Identity, Error, UpdateReactivationDateRequest>({
        mutationFn: (request) => services.identities.updateReactivationDate(id!, request),
        invalidate: [[IDENTITY_KEY, id], [IDENTITIES_KEY]],
        successMessage: i18n.identities.reactivationDateUpdated,
        closeDialog: closeReactivationDateDialog,
        onSuccess: () => {
            onReactivationDateSuccess?.()
        },
    })

    const evaluateRules = useEntityMutation<void, Error, void>({
        mutationFn: () => services.identities.evaluateRules(id!),
        successMessage: i18n.identities.evaluateRulesSubmitted,
        onSuccess: () => {
            onEvaluateRulesSuccess?.()
        },
    })

    const revoke = useEntityMutation<void, Error, string>({
        mutationFn: (assignmentId) => services.identities.revokeAssignment(id!, assignmentId),
        invalidate: [
            [IDENTITY_KEY, id, IDENTITY_SUBKEY_ASSIGNMENTS],
            [IDENTITY_KEY, id, IDENTITY_SUBKEY_TIMELINE],
        ],
        successMessage: i18n.identities.assignmentRevoked,
        closeDialog: closeRevokeAssignmentDialog,
        onSuccess: () => {
            onRevokeSuccess?.()
        },
    })

    const revokeFg = useEntityMutation<void, Error, string>({
        mutationFn: (assignmentId) => services.functionGroupAssignments.revoke(id!, assignmentId),
        invalidate: [IDENTITY_KEY, id, IDENTITY_SUBKEY_FG_ASSIGNMENTS],
        successMessage: i18n.functionGroupAssignments.assignmentRevoked,
        closeDialog: closeRevokeFgDialog,
        onSuccess: () => {
            onRevokeFgSuccess?.()
        },
    })

    const createAccount = useEntityMutation<unknown, Error, string>({
        mutationFn: (targetSystemId) => services.accounts.createAccount(id!, targetSystemId),
        invalidate: accountsQueryKey,
        successMessage: i18n.accounts.operationQueued,
        closeDialog: closeCreateAccountDialog,
        onSuccess: () => {
            onCreateAccountSuccess?.()
        },
    })

    const settleAccountAction = (accountId: string) => {
        queryClient.invalidateQueries({queryKey: accountsQueryKey})
        queryClient.invalidateQueries({queryKey: [ACCOUNT_STATUS_EVENTS_QUERY_KEY, accountId]})
        onAccountActionSettled?.(accountId)
    }

    // WHY: manual provision-now affordance per identity for SCIM/LDAP outbound
    // target systems. Re-issues the existing create-account provisioning task
    // (tier-3 path) so admins can retry a failed initial creation or kick a
    // stuck account from the UI (issue 736 B.1/B.2).
    const provisionNow = useEntityMutation<unknown, Error, {identityId: string; targetSystemId: string; accountId: string}>({
        mutationFn: ({identityId, targetSystemId}) => services.accounts.createAccount(identityId, targetSystemId),
        invalidate: accountsQueryKey,
        successMessage: i18n.accounts.provisionNowQueued,
        onSuccess: (_data, vars) => settleAccountAction(vars.accountId),
        onError: (_error, vars) => settleAccountAction(vars.accountId),
    })

    const disableAccount = useEntityMutation<unknown, Error, string>({
        mutationFn: (accountId) => services.accounts.disable(accountId, {reason: accountActionReason || null}),
        invalidate: accountsQueryKey,
        successMessage: i18n.lifecycleEvents.toast.accountUpdated,
        closeDialog: closeDisableAccountDialog,
        onSuccess: (_data, accountId) => settleAccountAction(accountId),
        onError: (_error, accountId) => settleAccountAction(accountId),
    })

    const enableAccount = useEntityMutation<unknown, Error, string>({
        mutationFn: (accountId) => services.accounts.enable(accountId, {reason: accountActionReason || null}),
        invalidate: accountsQueryKey,
        successMessage: i18n.lifecycleEvents.toast.accountUpdated,
        closeDialog: closeEnableAccountDialog,
        onSuccess: (_data, accountId) => settleAccountAction(accountId),
        onError: (_error, accountId) => settleAccountAction(accountId),
    })

    const deleteAccount = useEntityMutation<unknown, Error, string>({
        mutationFn: (accountId) => services.accounts.delete(accountId, {reason: accountActionReason || null}),
        invalidate: accountsQueryKey,
        successMessage: i18n.lifecycleEvents.toast.accountUpdated,
        closeDialog: closeDeleteAccountDialog,
        onSuccess: (_data, accountId) => settleAccountAction(accountId),
        onError: (_error, accountId) => settleAccountAction(accountId),
    })

    return {
        lifecycle,
        rehire,
        reactivationDate,
        evaluateRules,
        revoke,
        revokeFg,
        createAccount,
        provisionNow,
        disableAccount,
        enableAccount,
        deleteAccount,
    }
}
